import { Fragment, lazy, Suspense, useEffect, useRef, useState } from "react"

const StageScene = lazy(() => import("./StageScene"))

type StageProps = {
  graphicText?: string
}

export function Stage({ graphicText = "PLAYBRAIN SHARES GAME CULTURE" }: StageProps) {
  const words = graphicText.split(" ").filter(Boolean)
  const holderRef = useRef<HTMLDivElement | null>(null)
  const [isSceneIdle, setIsSceneIdle] = useState(false)
  const [isSceneVisible, setIsSceneVisible] = useState(false)
  const shouldMountScene = isSceneIdle && isSceneVisible

  useEffect(() => {
    if (typeof window === "undefined") return

    const requestIdle =
      window.requestIdleCallback ??
      ((callback: IdleRequestCallback) =>
        window.setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 0 }), 800))
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout
    const idleId = requestIdle(() => setIsSceneIdle(true), { timeout: 1600 })

    return () => cancelIdle(idleId)
  }, [])

  useEffect(() => {
    const holder = holderRef.current
    if (!holder || typeof window === "undefined") return

    if (!("IntersectionObserver" in window)) {
      setIsSceneVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setIsSceneVisible(true)
        observer.disconnect()
      },
      { rootMargin: "240px" }
    )
    observer.observe(holder)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <h1 className="absolute m-0 grid scale-x-100 scale-y-100 grid-cols-1 items-center justify-items-center font-[1000] text-[length:25lvh] text-[var(--white)] italic leading-[25lvh]">
        <StageHeadingWords words={words} />
      </h1>

      <div
        ref={holderRef}
        className="absolute top-[10%] left-0 h-4/5 w-full"
      >
        <Suspense
          fallback={
            <div
              className="h-full w-full"
              data-testid="stage-scene-fallback"
              aria-hidden="true"
            />
          }
        >
          {shouldMountScene ? <StageScene /> : null}
        </Suspense>
      </div>

      <div
        aria-hidden="true"
        data-testid="hero-heading-outline"
        className="pointer-events-none absolute m-0 grid scale-x-100 scale-y-100 grid-cols-1 items-center justify-items-center font-[1000] text-[length:25lvh] text-transparent italic leading-[25lvh] [-webkit-text-stroke-color:var(--white)] [-webkit-text-stroke-width:1px]"
      >
        <StageHeadingWords words={words} />
      </div>
    </>
  )
}

function StageHeadingWords({ words }: { words: string[] }) {
  return words.map((word, index) => (
    <Fragment key={`${word}-${index}`}>
      <span
        className={`mr-[calc(var(--letter-spacing)*-1)] translate-x-0 text-[length:var(--font-size)] leading-[calc(var(--font-size)*0.9)] tracking-[var(--letter-spacing)] transition-[font-size,line-height] duration-200 ease-out [animation-iteration-count:1] [animation-range:exit_0_exit_200px] [animation-timeline:view()] ${
          index === 0
            ? "[--font-size:min(15lvh,12lvw)] [--letter-spacing:0.12em] [animation:1ms_ease-in_hero-text-animate-1]"
            : index === 1
              ? "[--font-size:min(20lvh,16lvw)] [--letter-spacing:0.2em] [animation:1ms_ease-in_hero-text-animate-2]"
              : index === 2
                ? "[--font-size:min(24lvh,19.5lvw)] [--letter-spacing:0.4em] [animation:1ms_ease-in_hero-text-animate-1]"
                : "[--font-size:min(16lvh,13lvw)] [--letter-spacing:0.28em] [animation:1ms_ease-in_hero-text-animate-2]"
        }`}
      >
        {word}
      </span>
      {index < words.length - 1 ? " " : null}
    </Fragment>
  ))
}

export default Stage
